"use client";

/**
 * ChatMorphPanel — design language v2
 *
 * Port 1:1 del patrón morph de `LiquidPillDropdown` (líneas 263-410 del
 * archivo `liquid-pill-dropdown.tsx`). Diferencias mínimas respecto al
 * original, todas justificadas:
 *
 *  · El panel está anclado **bottom-right** en lugar de **top-right**
 *    (`absolute bottom-0 right-0` vs `absolute right-0 top-0`), porque el
 *    trigger del chat vive abajo a la derecha del viewport y debe crecer
 *    hacia arriba/izquierda.
 *  · El contenido es un layout libre (header + body) en lugar de un menú
 *    `LiquidPillGroupVertical`.
 *  · El `transform-origin` del panel es `bottom right` en lugar de
 *    `top right` (lo manejamos vía la clase global, igual que el original).
 *
 * Todo lo demás es idéntico:
 *  · Outer wrapper portado: `position: fixed` en las coords del trigger,
 *    dimensiones FIJAS al diámetro del trigger. Nunca se anima.
 *  · Inner panel: `position: absolute`, anclado a una esquina del wrapper,
 *    anima `min-width / max-width / min-height / max-height / padding`.
 *    NO anima `width`, `height` ni `border-radius` directamente.
 *  · `border-radius: min(50%, ${RADIUS}px)` ESTÁTICO. El morph
 *    circular→rectangular sale automático: a tamaño chico `50% < RADIUS` →
 *    círculo; a tamaño grande `50% >> RADIUS` → rounded-rect.
 *  · `overflow-hidden` en el panel animado — la `LiquidSurface` con
 *    `inset: 0; borderRadius: inherit` queda recortada al panel actual, así
 *    el surface ES visualmente el panel que crece.
 *  · State machine: `open` (portal montado) + `panelExpanded` (estilos
 *    expandidos). Apertura: setOpen(true) + rAF → setPanelExpanded(true).
 *    Cierre: setPanelExpanded(false) + setTimeout(CLOSE_MS) → setOpen(false).
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { LiquidPill } from "./liquid-pill";
import { LiquidSurface } from "./liquid-surface";
import { Z_INDEX } from "./z-index";
import type { CircleActionVariant } from "./circle-action-variants";

const OPEN_MS = 520;
const CLOSE_MS = 280;
// Misma curva que LiquidPillDropdown.
const EASE = "cubic-bezier(0.34, 1.2, 0.64, 1)";

// LiquidPill size="lg" circle diameter (de COLLAPSED_BOX del dropdown).
const TRIGGER_DIAMETER_PX = 44;
// Radio del rounded-rect cuando está expandido. `min(50%, RADIUS_PX)` hace
// el morph automático: a 44×44 → min(22, 22) = 22 ≈ círculo; a 320×520 →
// min(160, 22) = 22 → rounded-rect uniforme.
const RADIUS_PX = 22;
const PANEL_DEFAULT_W = 320;
const PANEL_DEFAULT_H = 520;
/**
 * Margen libre a respetar respecto al borde de la ventana cuando el panel
 * crece. El anclaje (bottom-right) ya queda contra el trigger (15px/10px de
 * margen según breakpoint), así que esta constante limita las dimensiones
 * para no chocar contra el borde opuesto (top/left).
 */
const PANEL_VIEWPORT_SAFE_MARGIN_PX = 10;

export interface ChatMorphPanelProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  triggerIcon: React.ReactNode;
  triggerVariant?: CircleActionVariant;
  triggerBadge?: React.ReactNode;
  triggerAriaLabel?: string;
  panelWidth?: number;
  panelHeight?: number;
  header?: React.ReactNode;
  children?: React.ReactNode;
}

export function ChatMorphPanel({
  open,
  onOpenChange,
  triggerIcon,
  triggerVariant = "indigo",
  triggerBadge,
  triggerAriaLabel,
  panelWidth = PANEL_DEFAULT_W,
  panelHeight = PANEL_DEFAULT_H,
  header,
  children,
}: ChatMorphPanelProps) {
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const closeTimerRef = React.useRef<number | null>(null);

  const [mounted, setMounted] = React.useState(false);
  // `open` interno: portal montado (panel en DOM, incluso si está encogiéndose).
  // `panelExpanded`: panel con estilos expandidos (dispara transitions).
  // Los separamos para correr la animación de salida antes de desmontar.
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [panelExpanded, setPanelExpanded] = React.useState(false);
  const [rect, setRect] = React.useState({ top: 0, left: 0 });

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const captureRect = React.useCallback(() => {
    if (!triggerRef.current || typeof window === "undefined") return;
    const r = triggerRef.current.getBoundingClientRect();
    setRect({ top: r.top, left: r.left });
  }, []);

  // Tamaño de viewport. Lo usamos para clamp del panel y para que el morph
  // se adapte a resoluciones bajas (objetivo: ocupar toda la pantalla salvo
  // un margen seguro respecto al borde opuesto al anclaje).
  const [viewport, setViewport] = React.useState({
    width:
      typeof window === "undefined" ? PANEL_DEFAULT_W * 4 : window.innerWidth,
    height:
      typeof window === "undefined" ? PANEL_DEFAULT_H * 2 : window.innerHeight,
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      // Reposicionamos el anclaje (cambia con resize/orientation).
      captureRect();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [captureRect]);

  // En resoluciones bajas, el trigger puede estar desplazado a la izquierda
  // para convivir con un CTA (+). Si el panel expandido siguiera anclado al
  // trigger, desperdiciaría todo el espacio que queda a su derecha. Por eso,
  // en mobile (< sm) mantenemos el trigger donde está, pero el panel expandido
  // se desplaza hasta los bordes seguros del viewport (10px) y usa casi todo
  // el ancho/alto disponible.
  const shouldUseViewportPanel = viewport.width < 640;
  const triggerRightEdge = rect.left + TRIGGER_DIAMETER_PX;
  const triggerBottomEdge = rect.top + TRIGGER_DIAMETER_PX;
  const panelAnchorRightEdge = shouldUseViewportPanel
    ? viewport.width - PANEL_VIEWPORT_SAFE_MARGIN_PX
    : triggerRightEdge;
  const panelAnchorBottomEdge = shouldUseViewportPanel
    ? viewport.height - PANEL_VIEWPORT_SAFE_MARGIN_PX
    : triggerBottomEdge;
  const panelShiftX = panelAnchorRightEdge - triggerRightEdge;
  const panelShiftY = panelAnchorBottomEdge - triggerBottomEdge;

  const effectivePanelWidth = React.useMemo(() => {
    const maxByViewport = Math.max(
      TRIGGER_DIAMETER_PX,
      panelAnchorRightEdge - PANEL_VIEWPORT_SAFE_MARGIN_PX,
    );
    return Math.min(panelWidth, maxByViewport);
  }, [panelAnchorRightEdge, panelWidth]);

  const effectivePanelHeight = React.useMemo(() => {
    const maxByViewport = Math.max(
      TRIGGER_DIAMETER_PX,
      panelAnchorBottomEdge - PANEL_VIEWPORT_SAFE_MARGIN_PX,
    );
    return Math.min(panelHeight, maxByViewport);
  }, [panelAnchorBottomEdge, panelHeight]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Sync prop `open` → estado interno (con close timer para animación de salida).
  React.useEffect(() => {
    if (open) {
      clearCloseTimer();
      captureRect();
      setInternalOpen(true);
      setPanelExpanded(false); // arranca colapsado, el rAF de abajo lo expande
    } else if (internalOpen) {
      clearCloseTimer();
      setPanelExpanded(false);
      closeTimerRef.current = window.setTimeout(() => {
        setInternalOpen(false);
        closeTimerRef.current = null;
      }, CLOSE_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!internalOpen) return;
    const raf = requestAnimationFrame(() => {
      setPanelExpanded(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [internalOpen]);

  React.useEffect(() => () => clearCloseTimer(), []);

  // Mantener anclado al trigger en scroll/resize.
  React.useEffect(() => {
    if (!internalOpen || typeof window === "undefined") return;
    captureRect();
    const update = () => captureRect();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    let ro: ResizeObserver | undefined;
    if (triggerRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(update);
      ro.observe(triggerRef.current);
    }
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [internalOpen, captureRect]);

  // Click outside / Escape.
  React.useEffect(() => {
    if (!internalOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      if (
        event.target instanceof Element &&
        event.target.closest("[data-open-chat-with-user]")
      ) {
        return;
      }
      onOpenChange(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [internalOpen, onOpenChange]);

  return (
    <>
      {/* Trigger in-flow — se desvanece cuando el portal está montado. */}
      <span
        ref={triggerRef}
        className={cn(
          "relative inline-block align-bottom",
          "chat-morph-trigger",
          internalOpen && "pointer-events-none opacity-0"
        )}
        style={{
          width: `${TRIGGER_DIAMETER_PX}px`,
          height: `${TRIGGER_DIAMETER_PX}px`,
        }}
      >
        <LiquidPill
          as="button"
          type="button"
          variant={triggerVariant}
          appearance="normal"
          shape="circle"
          size="lg"
          reactive
          // Igualamos el resolver de `LiquidFixedCircleButton`: con `reactive`
          // forzamos `surfaceStyle="default"` para usar el mismo glass que
          // los FABs (e.g. el botón "+" de Buy-Sell). Sin esto, `LiquidPill`
          // cae al default `sidebarInactive`, que tiene reglas dark-mode
          // propias (background/border/color) y hace que el chat se vea más
          // oscuro/saturado que un FAB equivalente.
          surfaceStyle="default"
          hoverBounce={!internalOpen}
          hoverDeform={!internalOpen}
          shadow="floating"
          icon={triggerIcon}
          onClick={() => onOpenChange(!open)}
          aria-label={triggerAriaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
        />
        {triggerBadge && (
          <span className="pointer-events-none absolute -right-0.5 -top-0.5">
            {triggerBadge}
          </span>
        )}
      </span>

      {/* Portal — outer wrapper FIJO en coords del trigger, dimensiones del trigger. */}
      {mounted &&
        internalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: `${TRIGGER_DIAMETER_PX}px`,
              height: `${TRIGGER_DIAMETER_PX}px`,
              zIndex: Z_INDEX.NAVBAR - 1,
            }}
          >
            {/*
              Inner panel — anclado bottom-right del wrapper. Anima
              min/max-width, min/max-height y padding desde el tamaño del
              trigger hasta el panel completo. El morph circle→rounded-rect
              sale gratis con `border-radius: min(50%, RADIUS_PX)`: a 44×44
              50% < RADIUS → círculo; a 320×520, RADIUS << 50% → rounded-rect.
              Mismo enfoque exacto que LiquidPillDropdown.
            */}
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="false"
              className={cn(
                "chat-morph-panel absolute bottom-0 right-0 overflow-hidden",
                panelExpanded && "chat-morph-panel--is-open"
              )}
              style={{
                transitionProperty:
                  "min-width, max-width, width, min-height, max-height, padding, box-shadow, transform",
                transitionDuration: `${panelExpanded ? OPEN_MS : CLOSE_MS}ms`,
                transitionTimingFunction: EASE,
                minWidth: panelExpanded
                  ? `${effectivePanelWidth}px`
                  : `${TRIGGER_DIAMETER_PX}px`,
                maxWidth: panelExpanded
                  ? `${effectivePanelWidth}px`
                  : `${TRIGGER_DIAMETER_PX}px`,
                minHeight: panelExpanded
                  ? `${effectivePanelHeight}px`
                  : `${TRIGGER_DIAMETER_PX}px`,
                maxHeight: panelExpanded
                  ? `${effectivePanelHeight}px`
                  : `${TRIGGER_DIAMETER_PX}px`,
                padding: 0,
                transform: panelExpanded
                  ? `translate3d(${panelShiftX}px, ${panelShiftY}px, 0)`
                  : "translate3d(0, 0, 0)",
                borderRadius: `min(50%, ${RADIUS_PX}px)`,
              }}
            >
              {/*
                LiquidSurface es el ÚNICO owner visual del panel.
                No superponemos fondos/capas manuales: el material, blur,
                tint, borde y shadow vienen del primitive.
              */}
              <LiquidSurface
                as="div"
                variant={triggerVariant}
                tone="tinted"
                shape="pill"
                size="free"
                interactive={false}
                cursorGlow={false}
                hoverDeform={false}
                hoverBounce={false}
                style={{ 
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                }}
              />

              {/* Superficie frosted idéntica a LiquidPillDropdown. */}
              <span
                aria-hidden
                className="chat-morph-panel-white pointer-events-none absolute inset-0"
                style={{
                  opacity: panelExpanded ? 1 : 0,
                  transition: `opacity ${panelExpanded ? OPEN_MS : CLOSE_MS}ms ${EASE}`,
                  borderRadius: "inherit",
                }}
              />

              {/* Contenido (header + body). `relative z-[3]` lo apila sobre
                  el LiquidSurface y la capa frosted. Anima `max-height: 0 → panelHeight` para
                  que el contenido se despliegue desde el tope a la vez que
                  se revela la máscara circular. */}
              <span className="relative z-[3] flex w-full min-w-0 flex-col items-stretch">
                <span
                  aria-hidden={!panelExpanded}
                  className="chat-morph-panel-content flex min-w-0 flex-col overflow-hidden"
                  style={{
                    transition: `max-height ${panelExpanded ? OPEN_MS : CLOSE_MS}ms ${EASE}, opacity ${Math.min(panelExpanded ? OPEN_MS : CLOSE_MS, 240)}ms ${EASE} ${panelExpanded ? 40 : 0}ms`,
                    maxHeight: panelExpanded ? `${effectivePanelHeight}px` : "0px",
                    opacity: panelExpanded ? 1 : 0,
                    pointerEvents: panelExpanded ? "auto" : "none",
                  }}
                >
                  {header && (
                    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200/40 bg-white/20 px-2 py-1.5 dark:border-zinc-800/40 dark:bg-zinc-900/20">
                      {header}
                    </div>
                  )}
                  {/* Wrapper de children sin overflow:hidden adicional —
                      el padre ya tiene overflow:hidden por la animación de
                      max-height. Una capa extra solo recortaba los efectos
                      de hover (bounce/deform) de las pills internas. */}
                  <div className="flex min-h-0 flex-1 flex-col">{children}</div>
                </span>
              </span>
            </div>
          </div>,
          document.body
        )}

      <style jsx global>{`
        /* Mismo tratamiento visual que LiquidPillDropdown expandido.
           Solo cambia transform-origin por el anclaje bottom-right del chat. */
        .chat-morph-panel > .liquid-surface {
          background: rgba(255, 255, 255, 0.72) !important;
          backdrop-filter: blur(24px) saturate(1.4);
          -webkit-backdrop-filter: blur(24px) saturate(1.4);
        }
        .dark .chat-morph-panel > .liquid-surface {
          background: rgba(16, 16, 20, 0.88) !important;
          backdrop-filter: blur(24px) saturate(1.02) brightness(0.68);
          -webkit-backdrop-filter: blur(24px) saturate(1.02) brightness(0.68);
        }
        .chat-morph-panel > .liquid-surface::before,
        .chat-morph-panel > .liquid-surface::after,
        .chat-morph-panel > .liquid-surface > .liquid-surface__hover-overlay,
        .chat-morph-panel > .liquid-surface > .liquid-surface__cursor-glow {
          display: none !important;
        }
        .chat-morph-panel-white {
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(20px) saturate(1.3);
          -webkit-backdrop-filter: blur(20px) saturate(1.3);
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
        }
        .dark .chat-morph-panel-white {
          background: rgba(16, 16, 20, 0.84);
          backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          -webkit-backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
        }
        .chat-morph-panel {
          transform-origin: bottom right;
          will-change: min-width, max-width, min-height, max-height, padding, transform;
        }
        .chat-morph-panel--is-open {
          box-shadow:
            0 1px 2px rgba(15,23,42,0.04),
            0 12px 28px -10px rgba(15,23,42,0.22),
            0 24px 48px -20px rgba(15,23,42,0.18) !important;
        }
        .dark .chat-morph-panel--is-open {
          box-shadow:
            0 1px 2px rgba(0,0,0,0.4),
            0 12px 28px -10px rgba(0,0,0,0.55),
            0 24px 48px -20px rgba(0,0,0,0.45) !important;
        }
        .chat-morph-panel--is-open > .liquid-surface::before,
        .chat-morph-panel--is-open > .liquid-surface::after {
          display: none;
        }
      `}</style>
    </>
  );
}

export default ChatMorphPanel;
